import React from 'react';
import { Lock } from 'lucide-react';
import { AutomationContentType } from 'custom-type';
import {
  ZAP_GOAL_TEMPLATES,
  ZapGoalId,
  isGoalAvailable,
} from '../zapGoalTemplates';

// unDraw illustrations, recoloured to the Zap purple. Licence and provenance:
// src/assets/images/zap-goals/SOURCE.txt
import goalLink from '../../../../../../assets/images/zap-goals/goal-link.svg';
import goalSell from '../../../../../../assets/images/zap-goals/goal-sell.svg';
import goalFaq from '../../../../../../assets/images/zap-goals/goal-faq.svg';
import goalLeads from '../../../../../../assets/images/zap-goals/goal-leads.svg';

/**
 * Step 1 of the Zap wizard: the outcome, before any mechanics.
 *
 * A grid rather than a stack — five stacked cards of title + sentence read as a
 * wall of text and pushed Next below the fold on a phone. An illustration says
 * what the goal is faster than its tagline does, so the tagline can shrink and
 * the whole choice fits on one screen.
 */

const GOAL_ART: Record<ZapGoalId, string> = {
  link: goalLink,
  sell: goalSell,
  faq: goalFaq,
  leads: goalLeads,
};

interface StepGoalProps {
  contentType: AutomationContentType;
  goal: ZapGoalId | null;
  setGoal: (goal: ZapGoalId) => void;
  entitlements: any;
  /** Locked template -> subscription screen, like the Zap hub's locked cards. */
  handleGoToUpgrade: () => void;
}

export function StepGoal({ contentType, goal, setGoal, entitlements, handleGoToUpgrade }: StepGoalProps) {
  const surface = contentType === 'story' ? 'story' : contentType === 'live' ? 'live' : 'post';

  return (
    <div className="space-y-4">
      <div>
        <h3 className="mb-2">What do you want this Zap to do?</h3>
        <p className="text-sm text-gray-500">
          Pick the outcome — we set the rest up for you.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
        {ZAP_GOAL_TEMPLATES.map((template) => {
          const available = isGoalAvailable(template, entitlements);
          const selected = goal === template.id;

          return (
            <div
              key={template.id}
              role="button"
              aria-label={template.title}
              onClick={() => {
                if (!available) {
                  handleGoToUpgrade();
                  return;
                }
                setGoal(template.id);
              }}
              className={`relative flex cursor-pointer flex-col overflow-hidden rounded-xl border bg-white transition-all ${
                selected
                  ? 'border-[#7c3aed] ring-1 ring-[#7c3aed]'
                  : 'border-gray-200 hover:border-purple-300'
              } ${available ? '' : 'opacity-70'}`}
            >
              {!available && (
                <span className="absolute right-1.5 top-1.5 z-10 inline-flex items-center gap-1 rounded-full bg-[#f3f0ff] px-2 py-[2px] text-[10px] font-semibold leading-none text-[#7c3aed]">
                  <Lock className="h-[10px] w-[10px]" />
                  Upgrade
                </span>
              )}

              <div
                className={`flex h-[84px] items-center justify-center px-3 py-2 ${
                  selected ? 'bg-[#f5f3ff]' : 'bg-[#fafafa]'
                }`}
              >
                <img
                  src={GOAL_ART[template.id]}
                  alt=""
                  aria-hidden="true"
                  className="max-h-full max-w-full object-contain"
                />
              </div>

              <div className="flex-1 px-2.5 py-2">
                <p className="mb-0 text-[13px] font-semibold leading-tight text-[#111]">
                  {template.title}
                </p>
                <p className="mb-0 mt-1 text-[11px] leading-snug text-gray-500">
                  {template.tagline}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-gray-400">
        This Zap only runs on the {surface}
        {surface === 'live' ? ' sessions' : 's'} you choose next.
      </p>
    </div>
  );
}

export default StepGoal;
